import zip from 'lodash/zip';
import each from 'lodash/each';
import times from 'lodash/times';
import maxBy from 'lodash/maxBy';
import sortBy from 'lodash/sortBy';
import indexOf from 'lodash/indexOf';
import identity from 'lodash/identity';
import property from 'lodash/property';
import cloneDeep from 'lodash/cloneDeep';

import * as d3 from 'd3';
import { interpolateBlues } from 'd3-scale-chromatic';

import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { MAX_CORRECT_BINS, barsMargin, canvasMargin, tooltipLeftOffset, tooltipTopOffset } from './constants';

const barsWidthMargin = barsMargin.left + barsMargin.right;
const canvasWidthMargin = canvasMargin.left + canvasMargin.right;
const barsHeightMargin = barsMargin.top + barsMargin.bottom;
const canvasHeightMargin = canvasMargin.top + canvasMargin.bottom;

const tooltipStyles = `
  background-color: #5d5d5d;
  border-radius: 5px;
  font-size: 12px;
  opacity: 0.94;
  padding: 6px;
  color: white;
`;

const BarChartComponent = props => {
  const node = useRef();

  useEffect(() => {
    const createBarChart = () => {
      const { bars, isColorGradient = true, isBarsThin } = props;

      const width = props.canvasWidth - canvasWidthMargin - barsWidthMargin;
      const height = props.canvasHeight - canvasHeightMargin - barsHeightMargin;

      let color = i => bars[i].color;
      const opacity = i => !(i % 5);
      const tooltip = d3.select('#tooltip');

      const x = d3
        .scaleBand()
        .padding(isBarsThin ? '0.6' : '0.1')
        .range([0, width])
        .domain(bars.map(property('name')));

      const y = d3
        .scaleLinear()
        .range([height, 0])
        .domain([0, d3.max(bars, property('count'))]);

      if (isColorGradient) {
        color = d3
          .scaleSequential(interpolateBlues)
          .domain([0, bars.length]);
      }

      const svg = d3
        .select(node.current)
        .select('#container')
        .attr('transform', `
          translate(
            ${canvasMargin.left},
            ${canvasMargin.top}
          )
        `);

      svg.select('#y-axis')
        .attr('class', 'y axis')
        .style('font-size', '14px')
        .call(d3.axisLeft(y));

      svg
        .select('#x-axis-container')
        .attr('transform', `
          translate(
            ${barsMargin.left},
            ${height + barsMargin.bottom}
          )
        `);

      svg
        .select('#x-axis')
        .style('font-size', '14px')
        .call(d3.axisBottom(x));

      if (props.xAxisLabel) {
        svg
          .select('#x-axis-label')
          .text(props.xAxisLabel)
          .attr('dx', width);
      }

      svg.selectAll('.bar').remove();

      svg
        .selectAll('.bar')
        .data(bars)
        .enter()
        .append('rect')
        .attr('class', 'bar')
        .attr('y', d => y(d.count))
        .attr('x', d => x(d.name) + barsMargin.left)
        .attr('height', d => height - y(d.count))
        .attr('width', x.bandwidth())
        .attr('fill', (_, i) => color(i))
        .on('mousemove', (event, d) => {
          tooltip.html(`
            <div style="${tooltipStyles}">
              <span>${d.name} (${d.count})</span>
            </div>
          `)
            .attr('class', 'absolute')
            .style('top', `${event.pageY - tooltipTopOffset}px`)
            .style('left', `${event.pageX - tooltipLeftOffset}px`);
        })
        .on('mouseleave', () => {
          tooltip.attr('class', 'hide');
        });

      if (bars.length > MAX_CORRECT_BINS) {
        svg
          .selectAll('#x-axis .tick')
          .attr('opacity', (_, i) => +opacity(i));
      }
    };

    const createStackedBarChart = () => {
      const width = props.canvasWidth - canvasWidthMargin - barsWidthMargin;
      const height = props.canvasHeight - canvasHeightMargin - barsHeightMargin;
      const bars = cloneDeep(props.bars);

      each(bars, bar => {
        bar.levels = sortBy(
          bar.levels, 'name'
        );
      });

      const barWithMaxLevelsCount = maxBy(bars, b => b.levels.length);
      const levelsNames = barWithMaxLevelsCount.levels.map(l => l.name);
      const maxLevelsCount = levelsNames.length;

      const x = d3
        .scaleBand()
        .padding(0.1)
        .rangeRound([0, width])
        .domain(bars.map(property('name')));

      const y = d3
        .scaleLinear()
        .rangeRound([height, 0])
        .domain([0, d3.max(bars, property('count'))]);

      const color = d3
        .scaleSequential(interpolateBlues)
        .domain([0, maxLevelsCount]);

      const tooltip = d3.select('#tooltip');

      const svg = d3
        .select(node.current)
        .select('#container.bar-chart')
        .attr('transform', `
          translate(
            ${canvasMargin.left},
            ${canvasMargin.top}
          )
        `);

      svg.select('#y-axis').style('font-size', '14px').attr('class', 'y axis').call(d3.axisLeft(y));

      svg
        .select('#x-axis-container')
        .attr('transform', `
          translate(
            ${barsMargin.left},
            ${height + barsMargin.bottom}
          )
        `);

      svg
        .select('#x-axis')
        .style('font-size', '14px')
        .call(d3.axisBottom(x));

      if (props.xAxisLabel) {
        svg
          .select('#x-axis-label')
          .text(props.xAxisLabel)
          .attr('dx', width);
      }

      svg.selectAll('.bar').remove();

      let stack = d3
        .stack()
        .keys(times(maxLevelsCount))
        .value((bar, i) => (bar.levels[i] && bar.levels[i].count) || 0)(bars);

      stack = zip(...stack);

      const bar = svg
        .selectAll('.bar')
        .data(stack)
        .enter()
        .append('g')
        .attr('class', 'bar');

      bar
        .selectAll('rect')
        .data(identity)
        .enter()
        .append('rect')
        .attr('data-level', (d, i) => JSON.stringify(d.data.levels[i]))
        .attr('y', d => y(d[1]) || 0)
        .attr('x', d => x(d.data.name) + barsMargin.left)
        .attr('height', d => (y(d[0]) - y(d[1])) || 0)
        .attr('width', x.bandwidth())
        .attr('fill', (d, i) => {
          const level = d.data.levels[i];

          return level ? color(indexOf(levelsNames, level.name)) : null;
        })
        .on('mousemove', event => {
          const level = JSON.parse(event.target.dataset.level);

          tooltip.html(`
            <div style="${tooltipStyles}">
              <span>${level.name} (${level.count})</span>
            </div>
          `)
            .attr('class', 'absolute')
            .style('top', `${event.pageY - tooltipTopOffset}px`)
            .style('left', `${event.pageX - tooltipLeftOffset}px`);
        })
        .on('mouseleave', () => {
          tooltip.attr('class', 'hide');
        });
    };

    if (props.stacked) {
      createStackedBarChart();
    } else {
      createBarChart();
    }
  });

  return (
    <svg
      ref={node}
      width={props.canvasWidth}
      height={props.canvasHeight}
      data-test="barChart"
    >
      <g id="container" className="bar-chart">
        <g id="x-axis-container">
          <g id="x-axis" />

          <text
            id="x-axis-label"

            dy="-5"
            fontSize="16"
            textAnchor="end"
          />
        </g>

        <g id="y-axis-container">
          <g id="y-axis" />

          <text
            id="y-axis-label"

            dy="15"
            fontSize="16"
            textAnchor="end"
            transform="rotate(-90)"
          >
            {props.yAxisLabel}
          </text>
        </g>
      </g>
    </svg>
  );
};


BarChartComponent.propTypes = {
  bars: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        count: PropTypes.number.isRequired
      }).isRequired,

      PropTypes.shape({
        name: PropTypes.number.isRequired,
        count: PropTypes.number.isRequired,
        color: PropTypes.string.isRequired
      }).isRequired,

      PropTypes.shape({
        name: PropTypes.string.isRequired,
        count: PropTypes.number.isRequired,
        levels: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string.isRequired,
            count: PropTypes.number.isRequired
          }).isRequired
        ).isRequired
      })
    ])
  ).isRequired,

  canvasWidth: PropTypes.number.isRequired,
  canvasHeight: PropTypes.number.isRequired,

  stacked: PropTypes.bool,
  isBarsThin: PropTypes.bool,
  isColorGradient: PropTypes.bool,

  xAxisLabel: PropTypes.string,
  yAxisLabel: PropTypes.string
};

BarChartComponent.defaultProps = {
  stacked: false, xAxisLabel: null, yAxisLabel: 'Count'
};

export default BarChartComponent;
